import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { ACTION_PLAN_V2 } from "@/components/common/ActionPlanV2/constants";
import { loadMucTieuList } from "@/components/common/ActionPlanV2/core/loadCongViecByMucTieu";
import { parseActionPlanDayjs } from "@/components/common/ActionPlanV2/core/date";
import type { ActionPlanAiPendingPlan } from "@/types/action-plan-v2/ActionPlanAiPlan";
import { useAuthStore } from "@/store/useAuthStore";
import { usePrivateStore } from "@/store/usePrivateStore";
import { toActionPlanApiDateIso } from "@/utils/dateOnlyForApi";

export type MucTieuSelectOption = { value: string; label: string };

export function planToDraftFormValues(plan: ActionPlanAiPendingPlan): Record<string, unknown> {
  const start = parseActionPlanDayjs(plan.ngayBatDau) ?? dayjs();
  const end = parseActionPlanDayjs(plan.ngayKetThuc) ?? start;
  return {
    mtctid: plan.mucTieuId || undefined,
    nguoiPhuTrachID: plan.nguoiPhuTrachId || undefined,
    tenCongViec: plan.tenCongViec || "",
    moTa: plan.moTa || "",
    dateRange: [start, end] as [Dayjs, Dayjs],
  };
}

function coerceRangeDayjs(raw: unknown): Dayjs | null {
  if (dayjs.isDayjs(raw)) return raw.isValid() ? raw : null;
  if (raw instanceof Date) return dayjs(raw);
  if (typeof raw === "string" && raw.trim()) {
    const parsed = parseActionPlanDayjs(raw);
    return parsed?.isValid() ? parsed : dayjs(raw);
  }
  return null;
}

function rangeToApiIso(range: unknown, which: 0 | 1): string | undefined {
  const arr = Array.isArray(range) ? range : null;
  const d = coerceRangeDayjs(arr?.[which]);
  if (!d?.isValid()) return undefined;
  return toActionPlanApiDateIso(d.toDate()) ?? undefined;
}

export function draftFormValuesToPlan(
  plan: ActionPlanAiPendingPlan,
  values: Record<string, unknown>,
  scope: { idChoice: string; year: number; phanHe: string },
): ActionPlanAiPendingPlan {
  const ngayBatDau = rangeToApiIso(values.dateRange, 0) ?? plan.ngayBatDau;
  const ngayKetThuc = rangeToApiIso(values.dateRange, 1) ?? plan.ngayKetThuc;
  return {
    ...plan,
    idChoice: scope.idChoice,
    year: scope.year,
    phanHe: scope.phanHe,
    mucTieuId: String(values.mtctid || plan.mucTieuId || "").trim(),
    nguoiPhuTrachId: String(values.nguoiPhuTrachID || plan.nguoiPhuTrachId || "").trim(),
    tenCongViec: String(values.tenCongViec || plan.tenCongViec || "").trim(),
    moTa: values.moTa != null ? String(values.moTa) : plan.moTa,
    ngayBatDau: ngayBatDau || undefined,
    ngayKetThuc: ngayKetThuc || undefined,
  };
}

export function validateActionPlanDraftPlan(plan: ActionPlanAiPendingPlan): string | null {
  if (plan.action === "update") {
    return validateActionPlanUpdateDraftPlan(plan);
  }
  if (!String(plan.tenCongViec || "").trim()) return "Nhập tên công việc.";
  if (!String(plan.mucTieuId || "").trim() && !String(plan.parentId || "").trim()) {
    return "Chọn mục tiêu gắn công việc.";
  }
  const ketThuc = String(plan.ngayKetThuc || "").trim();
  if (!ketThuc || !toActionPlanApiDateIso(ketThuc)) return "Chọn thời gian kết thúc hợp lệ.";
  if (!String(plan.nguoiPhuTrachId || "").trim()) return "Chọn người phụ trách.";
  return null;
}

export function validateActionPlanUpdateDraftPlan(plan: ActionPlanAiPendingPlan): string | null {
  if (!String(plan.taskId || "").trim()) return "Thiếu mã công việc.";
  if (!String(plan.tenCongViec || "").trim()) return "Nhập tên công việc.";
  const ketThuc = String(plan.ngayKetThuc || "").trim();
  if (!ketThuc || !toActionPlanApiDateIso(ketThuc)) return "Chọn thời gian kết thúc hợp lệ.";
  return null;
}

export function draftFormValuesToUpdatePlan(
  plan: ActionPlanAiPendingPlan,
  values: Record<string, unknown>,
  scope: { idChoice: string; year: number; phanHe: string },
): ActionPlanAiPendingPlan {
  const ngayBatDau = rangeToApiIso(values.dateRange, 0) ?? plan.ngayBatDau;
  const ngayKetThuc = rangeToApiIso(values.dateRange, 1) ?? plan.ngayKetThuc;
  return {
    ...plan,
    idChoice: scope.idChoice,
    year: scope.year,
    phanHe: scope.phanHe,
    tenCongViec: String(values.tenCongViec || plan.tenCongViec || "").trim(),
    moTa: values.moTa != null ? String(values.moTa) : plan.moTa,
    ngayBatDau: ngayBatDau || undefined,
    ngayKetThuc: ngayKetThuc || undefined,
  };
}

/** Load mục tiêu + đơn vị đo cho thẻ tạo việc trong chat AI. */
export function useActionPlanAiDraftScope(plan: ActionPlanAiPendingPlan) {
  const profileId = useAuthStore((s) => s.profile?.id);
  const idSelected = usePrivateStore((s) => s.getRequestHeader().selectTree?.idSelected || "");
  const storeYear = usePrivateStore((s) => s.giaiDoan?.year);
  const dateChoice = usePrivateStore((s) => s.giaiDoan?.dateChoice || undefined);
  const deptLabel = usePrivateStore(
    (s) =>
      s.appHeader?.departmentLabel ||
      s.treeSelectDepartment?.selectedValue?.label ||
      "",
  );
  const idSelect = String(idSelected || plan.idChoice || profileId || "").trim();
  const year = plan.year ?? storeYear ?? dayjs().year();
  const [mtOptions, setMtOptions] = useState<MucTieuSelectOption[]>([]);
  const [loadingMt, setLoadingMt] = useState(false);

  const loadMt = useCallback(async () => {
    if (!idSelect) return;
    setLoadingMt(true);
    try {
      const { items } = await loadMucTieuList({
        idSelect,
        year,
        dateChoice,
        filter: ACTION_PLAN_V2.TAB.PHU_TRACH,
      });
      const next = items.map((m) => ({ value: m.id, label: m.name }));
      setMtOptions((prev) => {
        if (
          prev.length === next.length &&
          prev.every((p, i) => p.value === next[i]?.value && p.label === next[i]?.label)
        ) {
          return prev;
        }
        return next;
      });
    } finally {
      setLoadingMt(false);
    }
  }, [idSelect, year, dateChoice]);

  useEffect(() => {
    void loadMt();
  }, [loadMt]);

  const scope = useMemo(
    () => ({
      idChoice: idSelect,
      year,
      phanHe: plan.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
      deptLabel: String(deptLabel || idSelect || "—"),
    }),
    [idSelect, year, plan.phanHe, deptLabel],
  );

  return { mtOptions, loadingMt, scope, profileId };
}
